import { useNavigate, createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { css } from "../../../../styled-system/css";
import { Box, Grid, VStack } from "../../../../styled-system/jsx";
import { Button } from "../../../components/core/basic/Button";
import Card from "../../../components/core/basic/Card";
import { PageContainer } from "../../../components/core/basic/PageContainer";
import Loading from "../../../components/core/blocks/Loading";
import ConfirmationModal from "../../../components/core/blocks/ConfirmationModal";
import ConnectSecondaryChannelWidget from "../../../modules/onboarding/components/ConnectSecondaryChannelWidget";
import { ShopifyStoresTable } from "../../../modules/onboarding/shopify/ShopifyStoresTable";
import { useShopifyConnectionStatus } from "../../../modules/onboarding/shopify/useShopifyConnectionStatus";
import { useDeleteShopifyConnection } from "../../../modules/onboarding/shopify/useDeleteShopifyConnection";
import { useSimpleCan } from "../../../config/casl/casl";
import UnauthorizedWarning from "../../../components/core/basic/UnauthorizedWarning";

function ShopifySummary() {
  const navigate = useNavigate();
  const { t } = useTranslation(["shopify"]);
  const canCreateOrg = useSimpleCan("create", "Org");

  const { data: connectionStatus, isLoading } = useShopifyConnectionStatus();
  const disconnectShopify = useDeleteShopifyConnection();

  if (isLoading) {
    return <Loading fullPage />;
  }

  if (!canCreateOrg) {
    return <UnauthorizedWarning />;
  }

  const isConnected = connectionStatus?.is_connected ?? false;
  const connectionType = connectionStatus?.connection_type ?? null;
  const statusText =
    !connectionType ? t(($) => $.shopify["Not Connected"])
    : connectionType === "custom_app" ? t(($) => $.shopify["Connected (Legacy)"])
    : t(($) => $.shopify["Connected"]);

  return (
    <PageContainer paddingX="xxlarge">
      <VStack gap="medium" paddingTop="xlarge" textAlign="center">
        <h1 className={css({ margin: 0, color: "#393939", fontSize: "34px", fontWeight: 700 })}>
          {t(($) => $.shopify["Shopify Onboarding"])}
        </h1>
        <p className={css({ margin: 0, fontSize: "medium" })}>
          {t(($) => $.shopify["Please confirm your following information is correct"])}
        </p>
      </VStack>

      <VStack gap="large" alignItems="stretch">
        <Card variant="flat" display="flex" flexDirection="column" gap="small">
          <h3 className={css({ margin: 0, fontSize: "large", fontWeight: 600 })}>
            {t(($) => $.shopify["Shopify Store Connection"])}
          </h3>
          <Box display="grid" gap="medium" alignItems="center" gridTemplateColumns={{ base: "1fr", lg: "3fr 1fr" }}>
            <VStack gap="small" alignItems="stretch">
              {connectionStatus?.store && (
                <Grid gap="medium" columns={2}>
                  <p className={css({ margin: 0, fontSize: "medium", fontWeight: 500 })}>
                    {t(($) => $.shopify["Store:"])}
                  </p>
                  <p className={css({ margin: 0, fontSize: "medium" })}>{connectionStatus.store}</p>
                </Grid>
              )}
              <Grid gap="medium" columns={2}>
                <p className={css({ margin: 0, fontSize: "medium", fontWeight: 500 })}>
                  {t(($) => $.shopify["Status:"])}
                </p>
                <p className={css({ margin: 0, fontSize: "medium" })}>{statusText}</p>
              </Grid>
            </VStack>
            <VStack gap="xsmall" alignItems="end">
              <Button
                onPress={() => {
                  void navigate({
                    to: "/onboarding/v2/connect",
                    search: { channel: "shopify", store: connectionStatus?.store || undefined },
                  });
                }}
              >
                {isConnected ? t(($) => $.shopify["Reconnect"]) : t(($) => $.shopify["Connect"])}
              </Button>
              {isConnected && (
                <ConfirmationModal
                  title={t(($) => $.shopify["Disconnect Shopify Store"])}
                  confirmationMessage={t(($) => $.shopify["Are you sure you want to disconnect your Shopify store?"])}
                  confirmButtonText={t(($) => $.shopify["Disconnect"])}
                  modalButtonText={t(($) => $.shopify["Disconnect"])}
                  modalButtonProps={{ variant: "link" }}
                  onConfirm={() => disconnectShopify.mutate()}
                />
              )}
            </VStack>
          </Box>
          {isConnected && connectionStatus?.missing_scopes && connectionStatus.missing_scopes.length > 0 && (
            <Box
              borderWidth="1px"
              borderColor="red.300"
              borderStyle="solid"
              borderRadius="md"
              padding="medium"
              backgroundColor="red.50"
            >
              <p className={css({ margin: 0, fontSize: "medium", fontWeight: 600, color: "red.700" })}>
                {t(($) => $.shopify["Missing Required Permissions"])}
              </p>
              <p className={css({ margin: 0, marginTop: "xsmall", fontSize: "small", color: "red.700" })}>
                {connectionStatus.connection_type === "oauth" ?
                  t(($) => $.shopify["Your Shopify app is missing the following scopes and needs to be re-authorized:"])
                : t(
                    ($) =>
                      $.shopify["Update the required scopes in your Shopify app settings, then refresh this page:"],
                  )
                }
              </p>
              <ul
                className={css({
                  margin: 0,
                  marginTop: "xsmall",
                  paddingLeft: "large",
                  fontSize: "small",
                  color: "red.700",
                })}
              >
                {connectionStatus.missing_scopes.map((scope) => (
                  <li key={scope}>{scope}</li>
                ))}
              </ul>
            </Box>
          )}
        </Card>

        <Card variant="flat" display="flex" flexDirection="column" gap="small">
          <Box display="grid" gap="medium" alignItems="center" gridTemplateColumns={{ base: "1fr", lg: "3fr 1fr" }}>
            <h3 className={css({ margin: 0, fontSize: "large", fontWeight: 600 })}>
              {t(($) => $.shopify["All Shopify Stores"])}
            </h3>
            <Box alignSelf="center" justifySelf="end">
              <Button onPress={() => void navigate({ to: "/onboarding/v2/connect", search: { channel: "shopify" } })}>
                {t(($) => $.shopify["Add New Store"])}
              </Button>
            </Box>
          </Box>
          <ShopifyStoresTable />
        </Card>

        <Card
          variant="flat"
          display="grid"
          gap="medium"
          alignItems="center"
          gridTemplateColumns={{ base: "1fr", lg: "3fr 1fr" }}
        >
          <VStack gap="small" alignItems="stretch">
            <ConnectSecondaryChannelWidget secondaryChannel="amazon" linkToSummaryView="/user/amzsummary" />
            <ConnectSecondaryChannelWidget secondaryChannel="walmart" linkToSummaryView="/user/walmartsummary" />
          </VStack>
          <Button
            onPress={() => void navigate({ to: "/user/dashboard" })}
            className={css({ alignSelf: "center", justifySelf: "end" })}
          >
            {t(($) => $.shopify["Go to Dashboard"])}
          </Button>
        </Card>
      </VStack>
    </PageContainer>
  );
}

export const Route = createFileRoute("/_authenticated/user/shopifysummary")({
  component: ShopifySummary,
  staticData: { application: "Admin" },
});
